/**
 * Theme — BELTURO Guest App white-label loader.
 *
 * The guest-app is served per tenant from its own subdomain
 * (e.g. felsenblick.belturo.app). The *fallback* design ships in the app
 * bundle (the BELTURO default brand). Each tenant may additionally ship a
 * small override stylesheet that re-defines a handful of CSS custom
 * properties — primary/brand color, page background, header/hero images,
 * corner radius — and nothing else.
 *
 * Because the design-system's semantic aliases (--brand, --accent,
 * --surface-page, borders, …) are all defined in terms of the raw scale
 * tokens (--green-*, --sand-*, --paper), a tenant theme only has to override
 * those scales (and the --ga-* image vars) to re-skin the entire app — every
 * component cascades automatically. See themes/_template.css for the full
 * contract and themes/maison-lavande.css for a worked example.
 *
 * Resolution:
 *   <slug>.belturo.app   → slug
 *   admin.belturo.app    → (reserved, no tenant theme)
 *   <slug>.localhost / ?tenant=x override for local development
 *
 * The override stylesheet is loaded by URL at runtime (it is NOT bundled):
 * the core-api GENERATES it from the design settings the host maintains in
 * the client-admin (Darstellung → Design & Branding) and serves it at
 * `<api>/theme/<slug>.css` (see core-api GuestAppTheme). A 404 — unknown
 * slug, unpublished accommodation or simply no customization — leaves the
 * bundled BELTURO fallback in place; the app never blocks on it. A static
 * override host can still be configured via config "themeBase" (the
 * hand-written themes in src/themes/ document that contract).
 */
export class Theme
{
    // Hostnames / first labels that are never a tenant.
    static RESERVED = [ "www", "admin", "app", "guest", "api", "static", "cdn" ];

    constructor( app )
    {
        this.app = app;
        this.slug = this._resolveSlug();
    }

    /**
     * The active tenant slug, or null when none could be resolved (local dev
     * without an override, bare apex domain, reserved host).
     */
    getTenantSlug()
    {
        return this.slug;
    }

    /**
     * Inject the tenant override stylesheet after the bundled styles so its
     * custom-property overrides win the cascade. No-op (keeps the fallback)
     * when there is no tenant slug. Safe to call once on boot.
     */
    apply()
    {
        // Tag the root so themes can scope overrides if they ever need to.
        document.documentElement.setAttribute( "data-tenant", this.slug || "default" );

        if ( !this.slug )
        {
            return;
        }

        if ( document.getElementById( "ga-theme" ) )
        {
            return;
        }

        // Default: the generated tenant stylesheet from the core-api;
        // "themeBase" stays available as a static-hosting override.
        const base = this.app.config.get( "themeBase", this.app.config.get( "api" ) + "/theme/" );
        const link = document.createElement( "link" );
        link.id = "ga-theme";
        link.rel = "stylesheet";
        // A <link> sends no headers — a preview token rides in the query.
        const href = base + encodeURIComponent( this.slug ) + ".css";
        link.href = href + ( this.app.preview ? this.app.preview.queryFor( href ) : "" );
        // No custom theme (404) must not break the app — keep the fallback.
        link.addEventListener( "error", () =>
        {
            console.info( `[theme] no override for "${this.slug}" — using fallback brand.` );
        } );
        document.head.appendChild( link );
    }

    /**
     * Sync the PWA/browser chrome color with the tenant's primary brand
     * color (called by App.resolveAccommodation once the theme is known).
     * Without a custom primary the bundled default stays in place.
     */
    applyMetaThemeColor( color )
    {
        if ( !color )
        {
            return;
        }
        let meta = document.querySelector( 'meta[name="theme-color"]' );
        if ( !meta )
        {
            meta = document.createElement( "meta" );
            meta.name = "theme-color";
            document.head.appendChild( meta );
        }
        meta.content = color;
    }

    /**
     * Use the tenant's uploaded app icon (theme.appIconUrl, a square PNG) as
     * favicon and iOS home-screen icon. Without one, no links are injected —
     * a full per-tenant PWA manifest is a later step.
     */
    applyAppIcon( url )
    {
        if ( !url )
        {
            return;
        }
        [ [ "icon", "ga-favicon" ], [ "apple-touch-icon", "ga-touch-icon" ] ].forEach( ( [ rel, id ] ) =>
        {
            let link = document.getElementById( id );
            if ( !link )
            {
                link = document.createElement( "link" );
                link.id = id;
                link.rel = rel;
                document.head.appendChild( link );
            }
            link.type = "image/png";
            link.href = url;
        } );
    }

    /**
     * Make the installable app the house's own: the manifest comes from the
     * API (name, app icon, colours of the house — core-api
     * GuestAppTheme.createManifest), and iOS, which reads no manifest names,
     * gets the house name as its home-screen title.
     *
     * `start` tells the API where this guest-app runs; the manifest URL stays
     * the same on every open, which Android needs to recognise an installed
     * app as this one. Without a tenant the static BELTURO manifest stays.
     */
    applyManifest( accommodation )
    {
        // A draft seen in preview is not to be installed.
        if ( !this.slug || !accommodation || accommodation.isPreview )
        {
            return;
        }

        const start = window.location.origin + window.location.pathname.replace( /[^/]*$/, "" );
        const link = document.getElementById( "ga-manifest" );
        if ( link )
        {
            link.href = this.app.config.get( "api" ) + "/theme/" + encodeURIComponent( this.slug ) +
                ".webmanifest?start=" + encodeURIComponent( start );
        }

        const title = document.querySelector( 'meta[name="apple-mobile-web-app-title"]' );
        if ( title && accommodation.name )
        {
            title.content = accommodation.name;
        }
    }

    _resolveSlug()
    {
        // Explicit override for local development: ?tenant=maison-lavande
        try
        {
            const q = new URLSearchParams( window.location.search ).get( "tenant" );
            if ( q )
            {
                return this._clean( q );
            }
        }
        catch ( e ) { /* no-op */ }

        const host = ( window.location.hostname || "" ).toLowerCase();

        // Bare host / IP / localhost → no tenant (fallback brand).
        if ( !host || host === "localhost" || /^[0-9.]+$/.test( host ) )
        {
            return null;
        }

        const labels = host.split( "." );

        // Local dev mirrors production subdomains as "<slug>.localhost"
        // (same convention as the client-admin login).
        if ( labels.length >= 2 && labels[ labels.length - 1 ] === "localhost" )
        {
            const localSlug = this._clean( labels[ 0 ] );
            return ( localSlug && !Theme.RESERVED.includes( localSlug ) ) ? localSlug : null;
        }

        // Need at least <slug>.<domain>.<tld>; a 2-label apex has no subdomain.
        if ( labels.length < 3 )
        {
            return null;
        }

        const slug = this._clean( labels[ 0 ] );
        if ( !slug || Theme.RESERVED.includes( slug ) )
        {
            return null;
        }
        return slug;
    }

    _clean( raw )
    {
        return String( raw ).toLowerCase().replace( /[^a-z0-9-]/g, "" );
    }
}
